import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { StatCardSkeleton } from '@deepguide-ai/ui/stat-card-skeleton'
import { OpenRequestsSkeleton } from '@/components/open-requests/OpenRequestsList'

/** Mirrors the personnel directory, without obsolete progress cards. */
export function AuditorPeopleSkeleton() {
  return (
    <div className='space-y-4'>
      <Skeleton className='h-6 w-24' />
      <Skeleton className='h-4 w-80 max-w-full' />
      <Skeleton className='h-3 w-full max-w-2xl' />
      <div className='flex flex-wrap items-center gap-3'>
        <Skeleton className='h-9 w-60 rounded-sm' />
        <Skeleton className='h-9 w-40 rounded-sm' />
        <Skeleton className='h-9 w-48 rounded-sm' />
      </div>
      <TableSkeleton rows={8} columns={8} className='bg-card' />
    </div>
  )
}

/** Mirrors `AuditorTimelineRoutePage` layout while `getDashboard` is pending. */
export function AuditorTimelineSkeleton() {
  return (
    <div className='space-y-4'>
      <div>
        <Skeleton className='h-8 w-28 rounded-sm' />
        <Skeleton className='mt-1 h-4 w-full max-w-xl rounded-sm' />
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <Skeleton className='h-4 w-24 rounded-sm' />
        {Array.from({ length: 7 }, (_, index) => (
          <Skeleton
            key={index}
            className='h-7 w-[4.5rem] rounded-full'
          />
        ))}
      </div>

      <AuditorTimelineTableSkeleton />
    </div>
  )
}

/** Mirrors the timeline's five-column activity table without duplicating page chrome. */
export function AuditorTimelineTableSkeleton() {
  return (
    <TableSkeleton
      rows={25}
      columns={5}
      headerHeight={40}
      rowHeight={40}
      columnWidths={['60%', '45%', '78%', '55%', '78%']}
      tableClassName='table-fixed text-[13px] [&_th]:px-4 [&_th]:py-2.5 [&_th:nth-child(1)]:w-[18%] [&_th:nth-child(2)]:w-[8%] [&_th:nth-child(3)]:w-[56%] [&_th:nth-child(4)]:w-[7%] [&_th:nth-child(5)]:w-[11%] [&_thead_tr]:bg-surface-subtle [&_thead_tr]:hover:bg-surface-subtle'
      rowClassName='bg-card hover:bg-card'
      cellClassName='px-4 py-2.5'
      className='typo-auditor-dashboard-activity overflow-hidden bg-card'
    />
  )
}


/** Mirrors `AuditorAuditEvidenceRoutePage` while `getDashboard` is pending. */
export function AuditorEvidenceSkeleton() {
  return (
    <div className='flex flex-col gap-6 p-6'>
      <div>
        <Skeleton className='h-7 w-28 rounded-sm' />
        <Skeleton className='mt-2 h-4 w-full max-w-xl rounded-sm' />
      </div>

      <div className='flex gap-3'>
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className='h-9 min-w-0 flex-1 rounded-md' />
        ))}
      </div>

      <TableSkeleton
        rows={10}
        columns={5}
        columnWidths={['3%', '48%', '24%', '15%', '10%']}
        tableClassName='table-fixed text-[12px]'
        className='bg-card'
      />
    </div>
  )
}

/** Mirrors `AuditorControlsRoutePage` split layout while `getDashboard` is pending. */
export function AuditorControlsSkeleton() {
  return (
    <div className='flex h-full min-h-0 w-full flex-col overflow-hidden bg-card'>
      <div className='flex shrink-0 items-center gap-4 border-b border-border px-4 py-2.5'>
        <Skeleton className='h-8 w-[280px] shrink-0 rounded-md' />
        <div className='flex flex-1 items-center gap-4 overflow-hidden'>
          <Skeleton className='h-4 w-32 shrink-0 rounded-sm' />
          <Skeleton className='h-4 w-28 shrink-0 rounded-sm' />
          <Skeleton className='ml-auto h-4 w-44 shrink-0 rounded-sm' />
        </div>
      </div>

      <div className='flex min-h-0 flex-1'>
        <div className='flex w-[308px] shrink-0 flex-col gap-2 border-r border-border p-3'>
          {Array.from({ length: 12 }, (_, index) => (
            <Skeleton key={index} className='h-10 w-full rounded-sm' />
          ))}
        </div>

        <div className='flex flex-1 flex-col gap-4 p-6'>
          <Skeleton className='h-7 w-48 rounded-sm' />
          <Skeleton className='h-4 w-full max-w-xl rounded-sm' />
          <Skeleton className='h-64 w-full rounded-sm' />
        </div>
      </div>
    </div>
  )
}

/**
 * Mirrors `AuditorRequestListRoutePage` (Information Requests) while
 * `getDashboard` is pending. Carries its own `p-6` — that route sets
 * `pageHeader.pad: false`, so the shell no longer pads the pending content.
 */
export function AuditorRequestListSkeleton() {
  return (
    <div className='flex flex-col gap-3 p-6'>
      <div>
        <Skeleton className='h-8 w-56 rounded-sm' />
        <Skeleton className='mt-2 h-4 w-full max-w-2xl rounded-sm' />
      </div>

      <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
        {Array.from({ length: 4 }, (_, index) => (
          <StatCardSkeleton key={index} variant='controls' />
        ))}
      </div>

      <TableSkeleton
        rows={8}
        columns={8}
        columnWidths={['18%', '26%', '12%', '8%', '12%', '10%', '7%', '7%']}
        tableClassName='text-[12px]'
        className='bg-card'
      />
    </div>
  )
}

/**
 * Mirrors `AuditorOpenRequestsRoutePage` (Inbox) while `getDashboard` is
 * pending — the two-pane, full-bleed shape, NOT a table. Same height math as
 * the page itself so the panels don't jump when the data lands.
 */
export function AuditorOpenRequestsSkeleton() {
  return (
    <div className='flex h-[calc(var(--app-vh)_-_50px)] overflow-hidden bg-card'>
      <div className='flex w-[360px] shrink-0 flex-col border-r border-border'>
        <div className='shrink-0 border-b border-border px-4 py-2.5'>
          <Skeleton className='h-4 w-20 rounded-sm' />
          <Skeleton className='mt-1.5 h-3 w-48 rounded-sm' />
        </div>
        {/* The list's own loading rows — same component the list renders while
            its query is in flight, so the two loading states are one thing. */}
        <OpenRequestsSkeleton rows={8} />
      </div>
      <div className='flex-1' />
    </div>
  )
}

/** Fallback for routes without a dedicated skeleton — never show loading text. */
export function AuditorCycleGenericSkeleton() {
  return (
    <div className='space-y-4'>
      <div>
        <Skeleton className='h-8 w-40 rounded-sm' />
        <Skeleton className='mt-1 h-4 w-full max-w-xl rounded-sm' />
      </div>

      <TableSkeleton
        rows={8}
        columns={5}
        columnWidths={['28%', '18%', '22%', '14%', '18%']}
        className='bg-card'
      />
    </div>
  )
}
